import { URL_FOR_ORGANIZATIONS, URL_FOR_SCHOOLS } from '../../../constants';
import { isCompanyOrChildCompany, isSchoolOrTeacher } from '../../../utilities';
import { findByUniqid, MenuNode, nodeTo } from '../Header/HeaderNew/menuData';

export type FooterVariant = 'guest' | 'retail' | 'school' | 'company';

export const getFooterVariant = (user: any): FooterVariant => {
  if (!user) {
    return 'guest';
  }
  if (isSchoolOrTeacher(user)) {
    return 'school';
  }
  if (isCompanyOrChildCompany(user)) {
    return 'company';
  }
  return 'retail';
};

export interface FooterBenefit {
  icon: string;
  label: string;
}

const BENEFITS_COMMON: {
  company: FooterBenefit;
  deliveryRetail: FooterBenefit;
  deliverySchool: FooterBenefit;
  products: FooterBenefit;
  shops: FooterBenefit;
  returns: FooterBenefit;
  discountSchool: FooterBenefit;
  discountCompany: FooterBenefit;
} = {
  company: { icon: 'hearth_full', label: 'Slovenská firma na trhu od 1996' },
  deliveryRetail: { icon: 'navigation', label: 'Doprava zadarmo už od 49 €' },
  deliverySchool: { icon: 'navigation', label: 'Doprava zadarmo do školy' },
  returns: { icon: 'info', label: 'Vrátenie tovaru do 14 dní' },
  discountSchool: { icon: 'info', label: 'Zľavy pre školy až do 40%' },
  discountCompany: { icon: 'info', label: 'Zľavy pre firmy až do 40%' },
  products: { icon: 'cog', label: '8000+ produktov na sklade' },
  shops: { icon: 'shop', label: 'Sieť predajní po celej SR' },
};

export const getFooterBenefits = (variant: FooterVariant): FooterBenefit[] => {
  const b = BENEFITS_COMMON;
  switch (variant) {
    case 'school':
      return [
        b.company,
        b.deliverySchool,
        b.discountSchool,
        b.products,
        b.shops,
      ];
    case 'company':
      return [
        b.company,
        b.deliveryRetail,
        b.discountCompany,
        b.products,
        b.shops,
      ];
    default:
      return [b.company, b.deliveryRetail, b.returns, b.products, b.shops];
  }
};

export interface FooterNavItem {
  name: string;
  link?: string;
  chat?: boolean;
}

export interface FooterNavColumn {
  key: string;
  title: string;
  items: FooterNavItem[];
}

export const getPrimaryColumn = (variant: FooterVariant): FooterNavItem[] => [
  { name: 'Úvod', link: '/' },
  { name: 'Predajne', link: '/predajne' },
  { name: 'Pre školské zariadenia', link: URL_FOR_SCHOOLS },
  { name: 'Firmy a organizácie', link: URL_FOR_ORGANIZATIONS },
  { name: 'Kontakty', link: '/kontakt' },
  variant === 'guest'
    ? { name: 'Registrácia / Prihlásenie', link: '/prihlasenie' }
    : { name: 'Môj účet', link: '/my-account' },
];

const SHOP_COLUMN: FooterNavColumn = {
  key: 'shop',
  title: 'Internetový obchod',
  items: [
    { name: 'Školské potreby', link: '/skolske-potreby' },
    { name: 'Kancelária', link: '/kancelaria' },
    { name: 'Domácnosť', link: '/domacnost' },
    { name: 'Tvorivosť', link: '/tvorivost' },
    { name: 'Párty', link: '/party' },
    { name: 'Písanie', link: '/pisanie' },
  ],
};

const HELP_COLUMN: FooterNavColumn = {
  key: 'help',
  title: 'Potrebujete poradiť?',
  items: [
    { name: 'Chat - spýtajte sa online', chat: true },
    { name: 'Často kladené otázky', link: '/casto-kladene-otazky' },
    { name: 'Obchodné podmienky', link: '/obchodne-podmienky' },
    { name: 'Reklamácie', link: '/ako-vybavit-reklamaciu' },
    { name: 'Ochrana súkromia (GDPR)', link: '/gdpr' },
    { name: 'Cookies', link: '/cookies' },
  ],
};

const CUSTOMER_COLUMN: FooterNavColumn = {
  key: 'customers',
  title: 'Pre zákazníkov',
  items: [
    { name: 'Zoznamy pomôcok Skvelko', link: '/sady' },
    { name: 'Aktuality', link: '/siet-papiernictiev/aktuality' },
    { name: 'Zákaznícka karta', link: '/karticka' },
    { name: 'Akcia / Novinky', link: '/akcia2' },
  ],
};

const SCHOOL_EQUIPMENT_COLUMN: FooterNavColumn = {
  key: 'school-equipment',
  title: 'Vybavenie škôl',
  items: [
    { name: 'Triedy a ŠKD', link: '/triedy-a-skd' },
    { name: 'Odborné učebne', link: '/odborne-ucebne' },
    { name: 'Akustické panely', link: '/kategoria-akusticke-panely' },
    { name: 'Šatne a chodby', link: '/satne-a-chodby' },
    { name: 'Materské školy', link: '/materske-skoly' },
    { name: 'Oddychové zóny', link: '/oddychove-zony' },
    { name: 'Jedálne', link: '/jedalne' },
  ],
};

const SCHOOL_OFFER_SOURCES: Array<{
  name: string;
  uniqid: string;
  fallback: string;
}> = [
  {
    name: 'Aktuality pre školy',
    uniqid: 'SCHOOL_ARTICLES',
    fallback: '/pre-skolske-zariadenia/aktuality-pre-skoly',
  },
  {
    name: 'Zoznamy pomôcok Skvelko',
    uniqid: 'SCHOOL_LIST_SKVELKO',
    fallback: '/pre-skolske-zariadenia/co-je-skvelko',
  },
  {
    name: 'Pomôcky pre žiakov',
    uniqid: 'SCHOOL_AIDS',
    fallback: '/pre-skolske-zariadenia/hmotna-nudza',
  },
  {
    name: 'Služby pre školy',
    uniqid: 'SCHOOL_SERVICES',
    fallback: '/pre-skolske-zariadenia/poradenstvo-pri-projektoch',
  },
  {
    name: 'Výhody spolupráce',
    uniqid: 'SCHOOL_COOPERATION',
    fallback: '/pre-skolske-zariadenia/velkoobchodne-ceny-v-eshope',
  },
  {
    name: 'Referencie a realizácie',
    uniqid: 'REFERENCES',
    fallback: '/pre-skolske-zariadenia/referencie-a-realizacie/triedy-a-skd',
  },
];

const buildSchoolOfferColumn = (
  mainMenuTree?: MenuNode[] | null,
): FooterNavColumn => ({
  key: 'school-offer',
  title: 'Ponuka pre školy',
  items: SCHOOL_OFFER_SOURCES.map((source) => ({
    name: source.name,
    link: nodeTo(findByUniqid(mainMenuTree, source.uniqid), source.fallback),
  })),
});

const COMPANY_OFFER_COLUMN: FooterNavColumn = {
  key: 'company-offer',
  title: 'Ponuka pre firmy',
  items: [
    { name: 'Výhody pre firmy', link: '/firmy-a-organizacie' },
    { name: 'Karta výhod', link: '/karta-vyhod' },
    { name: 'Doprava objednávok', link: '/doprava' },
    { name: 'Reklamné predmety', link: '/reklamne-predmety' },
    { name: 'Potlač kalendárov a diárov', link: '/potlac-kalendarov-a-diarov' },
    { name: 'Značkové darčekové perá', link: '/darcekove-pera' },
  ],
};

export const getFooterColumns = (
  variant: FooterVariant,
  mainMenuTree?: MenuNode[] | null,
): FooterNavColumn[] => {
  switch (variant) {
    case 'school':
      return [
        SHOP_COLUMN,
        HELP_COLUMN,
        SCHOOL_EQUIPMENT_COLUMN,
        buildSchoolOfferColumn(mainMenuTree),
      ];
    case 'company':
      return [SHOP_COLUMN, HELP_COLUMN, COMPANY_OFFER_COLUMN];
    default:
      return [SHOP_COLUMN, HELP_COLUMN, CUSTOMER_COLUMN];
  }
};

export const showFooterPayments = (variant: FooterVariant): boolean =>
  variant !== 'school';

export interface FooterPaymentLogo {
  label: string;
  src: string;
}

export const PAYMENT_LOGOS: FooterPaymentLogo[] = [
  { label: 'Visa', src: '/images/cart/payments/card-visa.png' },
  {
    label: 'Visa Electron',
    src: '/images/cart/payments/card-visa-electron.png',
  },
  { label: 'Mastercard', src: '/images/cart/payments/card-mastercard.png' },
  { label: 'Maestro', src: '/images/cart/payments/card-maestro.png' },
  { label: 'Apple Pay', src: '/images/cart/payments/card-apple-pay.png' },
  { label: 'Google Pay', src: '/images/cart/payments/card-google-pay.png' },
  { label: 'Tatra banka', src: '/images/cart/payments/tatra-banka.png' },
  {
    label: 'Slovenská sporiteľňa',
    src: '/images/cart/payments/slovenska-sporitelna.png',
  },
  { label: 'VÚB banka', src: '/images/cart/payments/vub-banka.png' },
  { label: 'ČSOB', src: '/images/cart/payments/csob.png' },
  { label: 'Poštová banka', src: '/images/cart/payments/postova-banka.png' },
  { label: 'Fio banka', src: '/images/cart/payments/fio-banka.png' },
  { label: 'Revolut', src: '/images/cart/payments/revolut.png' },
  {
    label: 'Raiffeisen Bank',
    src: '/images/cart/payments/raiffeisen-bank.png',
  },
  { label: 'N26', src: '/images/cart/payments/n26.png' },
];

export interface FooterSocialLink {
  label: string;
  icon: string;
  href: string;
  count: string;
}

export const SOCIAL_LINKS: FooterSocialLink[] = [
  {
    label: 'Facebook',
    icon: 'facebook_footer',
    href: 'https://www.facebook.com/daffer.sk',
    count: '8k+',
  },
  {
    label: 'YouTube',
    icon: 'youtube_footer',
    href: 'https://www.youtube.com/channel/UC80eR086bA9QVGPpVrpzfGg',
    count: '50+',
  },
  {
    label: 'LinkedIn',
    icon: 'linkedin_footer',
    href: 'https://www.linkedin.com/company/daffer/',
    count: '50+',
  },
  {
    label: 'Instagram',
    icon: 'instagram_footer',
    href: 'https://www.instagram.com/daffer_dobrepapiernictvo',
    count: '1,5k+',
  },
];
